/**
 * Escritas da planilha de Transações pelos repositórios do app (ver viewer/lib/editing.ts).
 * Cada método termina com `after()`: modelo novo nas telas + sync agendado.
 */
import { deleteTransaction, undeleteTransaction, updateTransaction } from '@/db/repositories/transactionRepository';
import { materializeOccurrence, settleOccurrence, skipOccurrence } from '@/db/repositories/recurringRepository';
import type { TxEditor, TxPatch } from '../viewer/lib/editing';
import type { TransactionRow } from '../viewer/lib/types';
import { askConfirm } from './confirm';
import { applySeriesDelete, applySeriesUpdate, chooseDeleteScope, chooseEditScope, isSeries, type SeriesChange } from './seriesScope';

/** Ocorrência projetada não existe no banco: concretiza na série e devolve o id real. */
async function realId(t: TransactionRow): Promise<string | null> {
  if (!t.isProjected) return t.id;
  if (!t.recurringId) return null;
  return materializeOccurrence(t.recurringId, t.recurringOccurrenceDate ?? t.date);
}

function toUpdate(p: TxPatch): Parameters<typeof updateTransaction>[1] {
  const data: Record<string, unknown> = {};
  if (p.date !== undefined) data.date = p.date;
  if (p.description !== undefined) data.description = p.description;
  if (p.amount !== undefined) data.amount = p.amount;
  // null limpa a coluna: o repositório grava o que recebe (o tipo diz string, o banco aceita null).
  if (p.categoryId !== undefined) data.categoryId = p.categoryId;
  if (p.subcategoryId !== undefined) data.subcategoryId = p.subcategoryId;
  if (p.accountId !== undefined) data.accountId = p.accountId;
  if (p.creditCardId !== undefined) data.creditCardId = p.creditCardId;
  if (p.invoiceMonth !== undefined) data.invoiceMonth = p.invoiceMonth;
  return data as Parameters<typeof updateTransaction>[1];
}

export function createTxEditor(after: () => Promise<void>): TxEditor {
  return {
    confirm: askConfirm,
    async update(rows, patch) {
      let n = 0;
      // Uma linha só, de série: pergunta o escopo como o app. Em lote, vale só a ocorrência.
      if (rows.length === 1 && isSeries(rows[0])) {
        const t = rows[0];
        const p = typeof patch === 'function' ? patch(t) : patch;
        if (!p) return 0;
        const change: SeriesChange = { date: p.date, amount: p.amount, accountId: p.accountId, creditCardId: p.creditCardId };
        const mode = await chooseEditScope(t, change);
        if (!mode) return 0;
        const ok = await applySeriesUpdate(t, toUpdate(p), mode);
        if (ok) await after();
        return ok ? 1 : 0;
      }
      for (const t of rows) {
        const p = typeof patch === 'function' ? patch(t) : patch;
        if (!p) continue;
        const id = await realId(t);
        if (!id) continue;
        await updateTransaction(id, toUpdate(p), 'single');
        n += 1;
      }
      if (n) await after();
      return n;
    },
    async setConfirmed(rows, confirmed) {
      let n = 0;
      for (const t of rows) {
        if (!!t.isConfirmed === confirmed) continue;
        if (t.isProjected && t.recurringId) {
          if (!confirmed) continue;
          await settleOccurrence({ id: t.recurringId, occurrenceDate: t.recurringOccurrenceDate ?? t.date });
        } else {
          await updateTransaction(t.id, { isConfirmed: confirmed }, 'single');
        }
        n += 1;
      }
      if (n) await after();
      return n;
    },
    async remove(rows) {
      const ids: string[] = [];
      if (rows.length === 1 && isSeries(rows[0])) {
        const mode = await chooseDeleteScope(rows[0]);
        if (!mode) return ids;
        const id = await applySeriesDelete(rows[0], mode);
        if (id) ids.push(id);
        await after();
        return ids;
      }
      for (const t of rows) {
        if (t.isProjected && t.recurringId) {
          await skipOccurrence(t.recurringId, t.recurringOccurrenceDate ?? t.date);
          continue;
        }
        await deleteTransaction(t.id, 'single');
        ids.push(t.id);
      }
      if (rows.length) await after();
      return ids;
    },
    async restore(ids) {
      for (const id of ids) await undeleteTransaction(id);
      if (ids.length) await after();
    },
  };
}
